import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, Dropdown, Layout, Menu, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/AuthContext';
import { roleLabel } from '../constants/roles';
import { filterNav, type NavItem } from './nav';

const { Header, Sider, Content } = Layout;

function toMenuItems(items: NavItem[]): MenuProps['items'] {
  return items.map((item) =>
    item.children && item.children.length > 0
      ? { key: item.key, label: item.label, children: toMenuItems(item.children) }
      : { key: item.key, label: item.label },
  );
}

interface SidebarLayoutProps {
  nav: NavItem[];
  title: string;
}

/** Shared sidebar shell used by both AppLayout and AdminLayout. */
export default function SidebarLayout({ nav, title }: SidebarLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const items = useMemo(() => (user ? toMenuItems(filterNav(nav, user.role)) : []), [nav, user]);

  // Open the submenu that contains the active route (e.g. /reports for /reports/trends).
  const openKeys = useMemo(() => {
    const seg = location.pathname.split('/').slice(0, 2).join('/');
    return seg ? [seg] : [];
  }, [location.pathname]);

  const userMenu: MenuProps = {
    items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'خروج' }],
    onClick: () => void logout(),
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={220} breakpoint="lg" collapsedWidth={0} theme="dark">
        <div style={{ color: '#fff', padding: '16px', fontWeight: 600, textAlign: 'center' }}>
          {title}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={openKeys}
          items={items}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingInline: 24,
          }}
        >
          <Typography.Text style={{ color: '#fff', fontSize: 16 }}>
            سامانه هوشمند پایش مرکز تماس
          </Typography.Text>
          {user && (
            <Dropdown menu={userMenu} placement="bottomLeft">
              <Button type="text" icon={<UserOutlined />} style={{ color: '#fff' }}>
                {user.name} {user.familyName} · {roleLabel(user.role)}
              </Button>
            </Dropdown>
          )}
        </Header>
        <Content style={{ margin: 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
